/**
 * FeedbackProvider — exposes ambient surface context (caseId, sessionKey,
 * current document, etc.) to deeply-nested feedback widgets so they don't
 * have to thread context through every prop layer.
 *
 * Mount this at the closest meaningful boundary (HomeLayout for global
 * scope, CaseWorkspaceLayout for case-scoped). The useFeedback hook reads
 * from this provider and merges in any per-call overrides.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { FeedbackSurfaceContext } from "@/types/feedback";

const FeedbackContextCtx = createContext<FeedbackSurfaceContext | null>(null);

interface FeedbackProviderProps {
  children: ReactNode;
  /** Ambient context fields that apply to every feedback emitted within. */
  context: FeedbackSurfaceContext;
}

export function FeedbackProvider({ children, context }: FeedbackProviderProps) {
  // Memo on the JSON shape so consumers don't re-render on referentially
  // unequal objects with identical fields.
  const value = useMemo(() => context, [JSON.stringify(context)]);
  return <FeedbackContextCtx.Provider value={value}>{children}</FeedbackContextCtx.Provider>;
}

/** Read the ambient context. Returns undefined when no provider is mounted. */
export function useAmbientFeedbackContext(): FeedbackSurfaceContext | undefined {
  return useContext(FeedbackContextCtx) ?? undefined;
}
